/**
 * Locale-aware date formatting utilities for display.
 */
import i18n from '@/i18n';
import { getIntlLocaleForContent, tForContent } from './locale';

export type DateInput = string | Date | null | undefined;

type DateLabelKey =
  | 'not_available'
  | 'invalid'
  | 'never'
  | 'present'
  | 'today'
  | 'expired';

type IntlFormatKind = 'date' | 'datetime' | 'time';

const DATE_PRESETS = {
  short: { year: 'numeric', month: 'short', day: 'numeric' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  weekday: { weekday: 'long', month: 'long', day: 'numeric' },
  monthYear: { month: 'short', year: 'numeric' },
  dateTime: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  time: { hour: '2-digit', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

function dateLabel(key: DateLabelKey): string {
  return i18n.t(`common:dates.${key}`);
}

/** Localized date-related label (e.g. "Present" / "Présent"). */
export function getDateLabel(key: DateLabelKey): string {
  return dateLabel(key);
}

/** Date label in CV source language (e.g. Présent on a French CV when UI is EN). */
export function getDateLabelForContent(
  key: DateLabelKey,
  contentLocale?: string | null,
): string {
  return tForContent(`common:dates.${key}`, contentLocale);
}

function parseDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;

  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;

  return date;
}

type ResolvedDate =
  | { status: 'ok'; date: Date }
  | { status: 'empty' }
  | { status: 'invalid' };

function resolveDate(value: DateInput): ResolvedDate {
  if (value == null || value === '') return { status: 'empty' };

  const date = parseDate(value);
  if (!date) return { status: 'invalid' };

  return { status: 'ok', date };
}

function formatIntl(
  date: Date,
  preset: Intl.DateTimeFormatOptions,
  kind: IntlFormatKind,
  contentLocale?: string | null,
): string {
  const locale = getIntlLocaleForContent(contentLocale);

  switch (kind) {
    case 'datetime':
      return date.toLocaleString(locale, preset);
    case 'time':
      return date.toLocaleTimeString(locale, preset);
    default:
      return date.toLocaleDateString(locale, preset);
  }
}

function formatWithPreset(
  value: DateInput,
  preset: Intl.DateTimeFormatOptions,
  kind: IntlFormatKind,
  labels: { empty?: DateLabelKey; invalid?: DateLabelKey },
  contentLocale?: string | null,
): string {
  const resolved = resolveDate(value);

  if (resolved.status === 'empty') {
    return labels.empty ? dateLabel(labels.empty) : '';
  }
  if (resolved.status === 'invalid') {
    return labels.invalid ? dateLabel(labels.invalid) : '';
  }

  return formatIntl(resolved.date, preset, kind, contentLocale);
}

/**
 * Format a date for display (e.g. "Feb 6, 2026" / "6 févr. 2026").
 * Returns localized "N/A" when missing and "Invalid date" on parse errors.
 */
export function formatDate(
  value: DateInput,
  options?: { uppercase?: boolean },
): string {
  let formatted = formatWithPreset(value, DATE_PRESETS.short, 'date', {
    empty: 'not_available',
    invalid: 'invalid',
  });

  if (options?.uppercase) {
    formatted = formatted.toUpperCase();
  }

  return formatted;
}

/** Uppercase plan expiration date, or localized "Never" when unset. */
export function formatPlanExpirationDate(value: string | null): string {
  return value ? formatDate(value, { uppercase: true }) : dateLabel('never');
}

/** Long month name (e.g. blog post dates). */
export function formatLongDate(value: DateInput): string {
  return formatWithPreset(value, DATE_PRESETS.long, 'date', {});
}

/** Weekday + long date (e.g. home page header). */
export function formatWeekdayDate(value: DateInput = new Date()): string {
  return formatWithPreset(value, DATE_PRESETS.weekday, 'date', {
    invalid: 'invalid',
  });
}

/** Date and time (e.g. "Feb 6, 2026, 3:45 PM"). */
export function formatDateTime(value: DateInput): string {
  return formatWithPreset(value, DATE_PRESETS.dateTime, 'datetime', {
    empty: 'not_available',
    invalid: 'invalid',
  });
}

/**
 * Compact relative age for dense UI: Today / Yesterday / Nd ago / date.
 * Prefer for recent activity only; older values fall back to formatDate.
 */
export function formatRelativeShort(value: DateInput): string {
  const resolved = resolveDate(value);
  if (resolved.status !== 'ok') return '';

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();
  const startOfTarget = new Date(
    resolved.date.getFullYear(),
    resolved.date.getMonth(),
    resolved.date.getDate()
  ).getTime();
  const diffDays = Math.round((startOfToday - startOfTarget) / 86_400_000);

  if (diffDays <= 0) return dateLabel('today');
  if (diffDays === 1) {
    return i18n.t('common:dates.yesterday', { defaultValue: 'Yesterday' });
  }
  if (diffDays < 7) {
    return i18n.t('common:dates.days_ago', {
      count: diffDays,
      defaultValue: `${diffDays}d ago`,
    });
  }

  return formatDate(value);
}

/** Time only (e.g. chat timestamps). */
export function formatTime(value: DateInput): string {
  return formatWithPreset(value, DATE_PRESETS.time, 'time', {
    invalid: 'invalid',
  });
}

/** Month and year (e.g. experience ranges). */
export function formatMonthYear(
  value: DateInput,
  contentLocale?: string | null,
): string {
  return formatWithPreset(value, DATE_PRESETS.monthYear, 'date', {}, contentLocale);
}

/** Long month + year for calendar headers (uses active UI locale). */
export function formatCalendarMonthYear(value: DateInput): string {
  return formatWithPreset(value, { month: 'long', year: 'numeric' }, 'date', {});
}

/** Human-readable countdown until an expiration date. */
export function formatDaysUntilExpiration(dateString: string | null): string | null {
  if (!dateString) return null;

  const expirationDate = parseDate(dateString);
  if (!expirationDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil(
    (expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) return dateLabel('expired');
  if (diffDays === 0) return dateLabel('today');

  const yearDiff = expirationDate.getFullYear() - today.getFullYear();
  const monthDiff = expirationDate.getMonth() - today.getMonth();
  const monthsDiff = yearDiff * 12 + monthDiff;

  if (diffDays >= 28) {
    const count = monthsDiff >= 1 ? monthsDiff : 1;
    return i18n.t('common:dates.months_count', { count });
  }

  return i18n.t('common:dates.days_count', { count: diffDays });
}

/** Check if a date string is valid. */
export function isValidDate(dateString: string | null | undefined): boolean {
  return parseDate(dateString) !== null;
}
